/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  useState, useEffect, useContext,
} from 'react';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';
import { UserContext } from '../../../contexts/User';
import { NotificationContext } from '../../../contexts/Notification';
import Toggle from '../../../components/Toggle';

const CB_STYLE = { width: 16, height: 16, accentColor: '#3d5a8a', flexShrink: 0, cursor: 'pointer' };
const SCREENSHOT_FREQ = ['None', '1x', '2x', '3x'];

function GlobalTeamSettings() {
  const { activeWorkspace } = useContext(UserContext);
  const {
    displayNotification,
  } = useContext(NotificationContext);
  const [policies, setPolicies] = useState([]);
  const [policyFlags, setPolicyFlags] = useState([]);
  const [webcamAudit, setWebcamAudit] = useState(0);
  const [webcamAuditFrequency, setWebcamAuditFrequency] = useState(0);
  const [screenshot, setScreenshot] = useState(0);
  const [blurScreenshot, setBlurScreenshot] = useState(0);
  const [screenshotFrequency, setScreenshotFrequency] = useState(0);
  const [manualTracking, setManualTracking] = useState(false);
  const frequency = ['None', 'Daily', 'Hourly', 'Weekly'];

  const reset = () => {
    policyFlags.fill(0);
    setWebcamAudit(0);
    setScreenshot(0);
    setBlurScreenshot(0);
    setManualTracking(false);
  };

  const getTeamSettings = async () => {
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getGlobalSettings(activeWorkspace.id),
      timeout: 60000,
    });

    if (response.success) {
      const setting = response.data.teamSettings;

      setting.policyIds.split(',').forEach((policyId) => {
        policies.forEach((policy, index) => {
          if (policy.id === parseInt(policyId, 10)) {
            policyFlags[index] = 1;
          }
        });
      });
      setWebcamAudit(setting.enabledWebcamAudit);
      frequency.forEach((period, index) => {
        if (period === setting.webcamAuditFrequency) {
          setWebcamAuditFrequency(index);
        }
      });
      setScreenshot(setting.enabledScreenShot);
      setBlurScreenshot(setting.enabledBlurScreenShot);
      setScreenshotFrequency(setting.screenShotFrequency);
      setManualTracking(setting.manualTracking || false);
    } else if (response.status !== 404) {
      // 404 just means this workspace hasn't saved global settings yet —
      // show the defaults rather than an error.
      displayNotification({
        message: 'An error occurs while getting Global Team Setting',
        severity: 'error',
      });
    }
  };

  const setTeamSettings = async (setting) => {
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.updateGlobalSettings(activeWorkspace.id),
      data: { setting },
    });
    if (response.success) {
      displayNotification({
        message: 'Global Team Setting has been saved successfully.',
        severity: 'success',
      });
      getTeamSettings();
    } else {
      displayNotification({
        message: 'An error occurs while saving Global Team Setting',
        severity: 'error',
      });
    }
  };

  const save = () => {
    const array = [];
    if (policyFlags) {
      policyFlags.forEach((flag, index) => {
        if (flag) array.push(policies[index].id);
      });
    }
    const setting = {
      policyIds: array.join(','),
      enabledWebcamAudit: webcamAudit,
      webcamAuditFrequency: frequency[webcamAuditFrequency],
      enabledScreenShot: screenshot,
      enabledBlurScreenShot: blurScreenshot,
      screenShotFrequency: screenshotFrequency,
      manualTracking,
    };

    setTeamSettings(setting);
  };

  const getPtoPolicies = async () => {
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.offTimePolicies(activeWorkspace.id),
      timeout: 60000,
    });

    if (response.success) {
      setPolicies(response.data.ptoPolicies);
      setPolicyFlags(new Array(response.data.ptoPolicies.length).fill(0));
    }
  };

  useEffect(() => {
    if (policies.length) getTeamSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policies]);

  const handleCheckboxChange = (id, isChecked) => {
    setPolicyFlags((prevFlags) => {
      const updatedFlags = [...prevFlags];

      updatedFlags[id] = isChecked;

      return updatedFlags;
    });
  };

  useEffect(() => {
    getPtoPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sectionTitle = 'text-sm font-semibold text-ink-900 mb-3';
  const checkRow = 'flex items-center gap-2.5 cursor-pointer select-none';
  const freqLabel = 'block text-xs font-medium text-ink-500 mb-2';

  return (
    <div className="px-4 py-4" style={{ fontFamily: 'Inter, sans-serif', maxWidth: 720 }}>
      <div className="mb-5">
        <h4 className="text-base font-semibold text-ink-900 mb-0.5">Global Team Settings</h4>
        <p className="text-sm text-ink-500">Set organization-wide policies for all teams.</p>
      </div>

      <div className="flex flex-col gap-5">
        {/* Time-Off Policies */}
        <section>
          <h5 className={sectionTitle}>Time-Off Policies</h5>
          {policies.length ? (
            <div className="flex flex-col gap-1">
              {policies.map((policy, index) => (
                <label key={policy.id} className={checkRow}>
                  <input
                    type="checkbox"
                    checked={!!policyFlags[index]}
                    onChange={(e) => handleCheckboxChange(index, e.target.checked)}
                    style={CB_STYLE}
                  />
                  <span className="text-sm text-ink-900">{policy.name}</span>
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-ink-500">No time-off policies yet.</p>
          )}
        </section>

        {/* Webcam Audit */}
        <section>
          <h5 className={sectionTitle}>Webcam Audit</h5>
          <label className={`${checkRow} mb-3`}>
            <input
              type="checkbox"
              checked={!!webcamAudit}
              onChange={() => { setWebcamAudit(!webcamAudit); setWebcamAuditFrequency(1); }}
              style={CB_STYLE}
            />
            <span className="text-sm text-ink-900">Enable Webcam Audit</span>
          </label>
          <span className={freqLabel}>Frequency</span>
          <Toggle
            key={`wf-${webcamAudit}-${webcamAuditFrequency}`}
            activeLabel={webcamAudit ? frequency[webcamAuditFrequency] : 'None'}
            options={[
              { label: 'None', onClick: () => setWebcamAuditFrequency(0) },
              { label: 'Daily', onClick: () => setWebcamAuditFrequency(1) },
              { label: 'Hourly', onClick: () => setWebcamAuditFrequency(2) },
              { label: 'Weekly', onClick: () => setWebcamAuditFrequency(3) },
            ]}
          />
        </section>

        {/* Screenshot */}
        <section>
          <h5 className={sectionTitle}>Screenshot</h5>
          <div className="flex flex-col gap-1 mb-3">
            <label className={checkRow}>
              <input
                type="checkbox"
                checked={!!screenshot}
                onChange={() => { setScreenshot(!screenshot); setScreenshotFrequency(1); }}
                style={CB_STYLE}
              />
              <span className="text-sm text-ink-900">Enable Screenshot</span>
            </label>
            <label className={checkRow}>
              <input
                type="checkbox"
                checked={!!blurScreenshot}
                onChange={() => setBlurScreenshot(!blurScreenshot)}
                style={CB_STYLE}
              />
              <span className="text-sm text-ink-900">Automatically Blur Screenshots</span>
            </label>
          </div>
          <span className={freqLabel}>Frequency</span>
          <Toggle
            key={`sf-${screenshot}-${screenshotFrequency}`}
            activeLabel={screenshot ? SCREENSHOT_FREQ[screenshotFrequency] : 'None'}
            options={[
              { label: 'None', onClick: () => setScreenshotFrequency(0) },
              { label: '1x', onClick: () => setScreenshotFrequency(1) },
              { label: '2x', onClick: () => setScreenshotFrequency(2) },
              { label: '3x', onClick: () => setScreenshotFrequency(3) },
            ]}
          />
        </section>

        {/* Activity Tracking */}
        <section>
          <h5 className={sectionTitle}>Activity Tracking</h5>
          <label className={checkRow}>
            <input
              type="checkbox"
              checked={manualTracking}
              onChange={() => setManualTracking(!manualTracking)}
              style={CB_STYLE}
            />
            <span className="text-sm text-ink-900">Enable Manual Tracking (Users can start/stop tracking)</span>
          </label>
          <p className="text-xs text-ink-500 mt-2 whitespace-nowrap">
            When enabled, users will see start/stop buttons to control activity tracking. When disabled, tracking runs automatically.
          </p>
        </section>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center justify-center box-border h-10 px-4 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition"
            style={{ borderStyle: 'solid' }}
          >
            Reset to Defaults
          </button>
          <button
            type="button"
            onClick={save}
            className="inline-flex items-center justify-center box-border h-10 px-4 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] transition"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default GlobalTeamSettings;
